/*
 * Colouring Pages — canvas studio for Shopify Dawn
 * ------------------------------------------------
 * Works with sections/colouring-pages.liquid.
 * No dependencies. Pointer events => mouse, pen and touch all supported.
 */
(function () {
  'use strict';

  var DEFAULT_PALETTE = [
    '#000000', '#4A4A4A', '#9B9B9B', '#FFFFFF',
    '#7B2D26', '#C0392B', '#E74C3C', '#F5B7B1',
    '#B9531F', '#E67E22', '#F39C12', '#F9E79F',
    '#7D6608', '#F1C40F', '#F7DC6F', '#FCF3CF',
    '#145A32', '#27AE60', '#7DCEA0', '#D5F5E3',
    '#0E6251', '#16A085', '#48C9B0', '#A3E4D7',
    '#154360', '#2980B9', '#5DADE2', '#AED6F1',
    '#4A235A', '#8E44AD', '#BB8FCE', '#E8DAEF',
    '#78281F', '#EC7063', '#F1948A', '#FADBD8',
    '#4E342E', '#8D6E63', '#C8A27A', '#EFDCC6'
  ];

  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }

  function hexToRgb(hex) {
    var h = String(hex || '#000').replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) n = 0;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  function makeCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
  }

  /* ------------------------------------------------------------------ */
  /* Studio                                                              */
  /* ------------------------------------------------------------------ */

  function Studio(app, page) {
    this.app = app;
    this.t = app.t;
    this.page = page;
    this.el = app.studio;

    this.view = this.el.querySelector('[data-cp-canvas]');
    this.vctx = this.view.getContext('2d');
    this.stage = this.view.parentNode;
    this.ring = this.el.querySelector('[data-cp-ring]');
    this._lastPointer = null;
    this._flashing = false;

    this.zoom = 1;
    this.MAX_ZOOM = 6;
    this.pointers = new Map();     // live pointers, for pinch
    this.gesture = null;

    this.tool = 'brush';
    this.color = '#E74C3C';
    this.size = 26;
    this.softness = 0.55;      // 0 = hard edge, 1 = airbrush
    this.opacity = 1;          // 0 = invisible, 1 = solid colour
    this.tolerance = 30;       // fill tolerance 0-100
    this.tainted = false;      // true if the image blocked pixel access

    this.history = [];
    this.future = [];
    this.historyBytes = 0;
    this.HISTORY_BYTE_BUDGET = 48 * 1024 * 1024;

    this.dirty = null;
    this.drawing = false;
    this.pointerId = null;
    this._histAtStroke = 0;

    this.bindUi();
    this.load();
  }

  Studio.prototype.load = function () {
    var self = this;
    this.setBusy(true);
    this.el.querySelector('[data-cp-title]').textContent = this.page.title || '';

    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';

    img.onload = function () { self.build(img); };
    img.onerror = function () {
      // Retry without CORS: we can still show and paint, but pixel reads are blocked.
      var plain = new Image();
      plain.onload = function () { self.tainted = true; self.build(plain); };
      plain.onerror = function () { self.fail(); };
      plain.src = self.page.src;
    };
    img.src = this.page.src;
  };

  Studio.prototype.fail = function () {
    this.setBusy(false);
    var s = this.el.querySelector('[data-cp-status]');
    s.hidden = false;
    s.textContent = this.t.error_load;
  };

  Studio.prototype.setBusy = function (on) {
    var l = this.el.querySelector('[data-cp-loading]');
    if (l) l.hidden = !on;
  };

  /* Build the layers from the source line art. -------------------------- */
  Studio.prototype.build = function (img) {
    var max = this.app.data.settings.max_px || 1400;
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    var scale = Math.min(1, max / Math.max(iw, ih));
    var w = Math.max(1, Math.round(iw * scale));
    var h = Math.max(1, Math.round(ih * scale));

    this.w = w; this.h = h;

    this.view.width = w;
    this.view.height = h;
    this.view.style.aspectRatio = w + ' / ' + h;

    this.paint = makeCanvas(w, h);
    this.pctx = this.paint.getContext('2d', { willReadFrequently: true });

    /* The stroke in progress is laid down here at full strength and folded
       into the paint layer once, at the chosen opacity, when the stroke ends.
       Stamping straight onto the paint at partial opacity would darken every
       place two dabs overlap — which, since a stroke is hundreds of overlapping
       dabs, means a half-transparent brush that paints almost solid. */
    this.wet = makeCanvas(w, h);
    this.wctx = this.wet.getContext('2d');
    this.wetBox = null;

    // Mirror of the paint layer as it was before the current stroke.
    this.prev = makeCanvas(w, h);
    this.prevCtx = this.prev.getContext('2d');

    this.line = makeCanvas(w, h);
    var lctx = this.line.getContext('2d', { willReadFrequently: true });
    lctx.drawImage(img, 0, 0, w, h);

    // Turn the artwork into an alpha mask: white -> transparent, dark -> opaque.
    this.lineAlpha = new Uint8Array(w * h);
    if (!this.tainted) {
      try {
        var d = lctx.getImageData(0, 0, w, h);
        var px = d.data;
        var WHITE = 244, BLACK = 64, span = WHITE - BLACK;
        for (var i = 0, p = 0; i < this.lineAlpha.length; i++, p += 4) {
          var lum = (px[p] * 299 + px[p + 1] * 587 + px[p + 2] * 114) / 1000;
          var a = ((WHITE - lum) / span) * 255;
          a = a < 0 ? 0 : (a > 255 ? 255 : a);
          a = (a * px[p + 3]) / 255;      // respect any transparency in the source
          var v = a | 0;
          this.lineAlpha[i] = v;
          px[p] = 0; px[p + 1] = 0; px[p + 2] = 0; px[p + 3] = v;
        }
        lctx.putImageData(d, 0, 0);
      } catch (e) {
        this.tainted = true;
      }
    }

    if (this.tainted) {
      // Fall back to multiply blending; fill, download and entry need pixels,
      // so those are disabled and the shopper is told why.
      this.view.classList.add('cp-canvas--tainted');
      var s = this.el.querySelector('[data-cp-status]');
      s.hidden = false;
      s.textContent = this.t.error_pixels;
      ['[data-cp-tool="fill"]', '[data-cp-download]', '[data-cp-print]', '[data-cp-share]', '[data-cp-enter]']
        .forEach(function (sel) {
          var b = this.el.querySelector(sel);
          if (b) { b.disabled = true; b.hidden = true; }
        }, this);
    }

    this.zoom = 1;
    this.applyZoom();

    this.setBusy(false);
    this.render();
    this.updateUi();
  };

  /* Composite ----------------------------------------------------------- */
  /* Paint, then the stroke in progress at its opacity, then white behind the
     lot, then the ink on top. The white goes on with destination-over so an
     eraser stroke can punch through the paint above it without taking the
     background with it. */
  Studio.prototype.compose = function (c) {
    c.globalAlpha = 1;
    c.globalCompositeOperation = 'source-over';
    c.clearRect(0, 0, this.w, this.h);
    c.drawImage(this.paint, 0, 0);

    if (this.wetBox) {
      c.globalAlpha = this.opacity;
      c.globalCompositeOperation = (this.wetTool === 'eraser') ? 'destination-out' : 'source-over';
      c.drawImage(this.wet, 0, 0);
      c.globalAlpha = 1;
    }

    c.globalCompositeOperation = 'destination-over';
    c.fillStyle = '#ffffff';
    c.fillRect(0, 0, this.w, this.h);

    c.globalCompositeOperation = this.tainted ? 'multiply' : 'source-over';
    c.drawImage(this.line, 0, 0);
    c.globalCompositeOperation = 'source-over';
  };

  Studio.prototype.render = function () {
    this.compose(this.vctx);
  };

  /* Flattened copy on white, for download / print / entry. --------------- */
  Studio.prototype.flatten = function () {
    var out = makeCanvas(this.w, this.h);
    this.compose(out.getContext('2d'));
    return out;
  };

  /* Fold the stroke in progress into the paint layer, once, at the chosen
     opacity. Called before anything reads or snapshots the paint layer. */
  Studio.prototype.flushWet = function () {
    var b = this.wetBox;
    if (!b) return;
    this.wetBox = null;
    var w = b.x1 - b.x0, h = b.y1 - b.y0;
    if (w <= 0 || h <= 0) return;
    var c = this.pctx;
    c.globalAlpha = this.opacity;
    c.globalCompositeOperation = (this.wetTool === 'eraser') ? 'destination-out' : 'source-over';
    c.drawImage(this.wet, b.x0, b.y0, w, h, b.x0, b.y0, w, h);
    c.globalAlpha = 1;
    c.globalCompositeOperation = 'source-over';
    this.wctx.clearRect(b.x0, b.y0, w, h);
  };

  /* History ------------------------------------------------------------- */
  Studio.prototype.markDirty = function (x0, y0, x1, y1) {
    x0 = clamp(Math.floor(x0), 0, this.w); y0 = clamp(Math.floor(y0), 0, this.h);
    x1 = clamp(Math.ceil(x1), 0, this.w); y1 = clamp(Math.ceil(y1), 0, this.h);
    if (x1 <= x0 || y1 <= y0) return;
    if (!this.dirty) { this.dirty = { x0: x0, y0: y0, x1: x1, y1: y1 }; return; }
    var d = this.dirty;
    if (x0 < d.x0) d.x0 = x0;
    if (y0 < d.y0) d.y0 = y0;
    if (x1 > d.x1) d.x1 = x1;
    if (y1 > d.y1) d.y1 = y1;
  };

  Studio.prototype.commit = function () {
    this.flushWet();       // the stroke must be in the paint layer to be saved
    if (!this.dirty) return;
    var d = this.dirty; this.dirty = null;
    var w = d.x1 - d.x0, h = d.y1 - d.y0;
    if (w <= 0 || h <= 0) return;

    var before = this.prevCtx.getImageData(d.x0, d.y0, w, h);
    var after = this.pctx.getImageData(d.x0, d.y0, w, h);

    this.history.push({ x: d.x0, y: d.y0, before: before, after: after });
    this.historyBytes += before.data.length * 2;
    while (this.history.length > 1 && this.historyBytes > this.HISTORY_BYTE_BUDGET) {
      var drop = this.history.shift();
      this.historyBytes -= drop.before.data.length * 2;
    }
    this.future.length = 0;

    // Keep the mirror in sync with the committed state.
    this.prevCtx.putImageData(after, d.x0, d.y0);
    this.updateUi();
  };

  Studio.prototype.undo = function () {
    var e = this.history.pop();
    if (!e) return;
    this.historyBytes -= e.before.data.length * 2;
    this.pctx.putImageData(e.before, e.x, e.y);
    this.prevCtx.putImageData(e.before, e.x, e.y);
    this.future.push(e);
    this.render();
    this.updateUi();
  };

  Studio.prototype.redo = function () {
    var e = this.future.pop();
    if (!e) return;
    this.historyBytes += e.before.data.length * 2;
    this.pctx.putImageData(e.after, e.x, e.y);
    this.prevCtx.putImageData(e.after, e.x, e.y);
    this.history.push(e);
    this.render();
    this.updateUi();
  };

  Studio.prototype.clear = function () {
    this.markDirty(0, 0, this.w, this.h);
    this.wctx.clearRect(0, 0, this.w, this.h);
    this.wetBox = null;
    this.pctx.clearRect(0, 0, this.w, this.h);
    this.commit();
    this.render();
  };

  /* Brush --------------------------------------------------------------- */
  Studio.prototype.brushSprite = function () {
    var r = Math.max(1, this.size / 2);
    var key = [this.tool, this.color, Math.round(r * 100), Math.round(this.softness * 100)].join('|');
    if (this._spriteKey === key) return this._sprite;

    var pad = 2;
    var d = Math.ceil(r * 2) + pad * 2;
    var s = makeCanvas(d, d);
    var c = s.getContext('2d');
    var cx = d / 2, cy = d / 2;
    var rgb = hexToRgb(this.tool === 'eraser' ? '#000000' : this.color);

    if (this.softness <= 0.02) {
      c.fillStyle = 'rgb(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ')';
      c.beginPath(); c.arc(cx, cy, r, 0, Math.PI * 2); c.fill();
    } else {
      /* The solid core shrinks fast as softness rises, and the peak alpha drops
         with it, so a high setting behaves like an airbrush that builds up
         rather than a hard disc with a feathered rim. Paint never reaches past
         r, so the ring drawn at the nominal size is a true outer bound. */
      var inner = clamp(Math.pow(1 - this.softness, 2.6), 0, 0.85);
      var peak = 1 - this.softness * 0.55;
      var rgba = function (a) {
        return 'rgba(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ',' + (peak * a).toFixed(3) + ')';
      };
      var g = c.createRadialGradient(cx, cy, r * inner, cx, cy, r);
      g.addColorStop(0.00, rgba(1));
      g.addColorStop(0.20, rgba(0.80));
      g.addColorStop(0.40, rgba(0.55));
      g.addColorStop(0.60, rgba(0.32));
      g.addColorStop(0.78, rgba(0.15));
      g.addColorStop(0.90, rgba(0.05));
      g.addColorStop(1.00, rgba(0));
      c.fillStyle = g;
      c.fillRect(0, 0, d, d);
    }

    this._spriteKey = key;
    this._sprite = { canvas: s, r: r, half: d / 2 };
    return this._sprite;
  };

  /* Dabs land on the wet layer at full strength; the opacity is applied once,
     to the finished stroke. Going back over your own stroke therefore cannot
     push it past the opacity you asked for — only a second stroke can. */
  Studio.prototype.stamp = function (x, y) {
    var sp = this.brushSprite();
    var c = this.wctx;
    c.globalCompositeOperation = 'source-over';
    c.drawImage(sp.canvas, x - sp.half, y - sp.half);
    var x0 = x - sp.half - 1, y0 = y - sp.half - 1;
    var x1 = x + sp.half + 1, y1 = y + sp.half + 1;
    this.markWet(x0, y0, x1, y1);
    this.markDirty(x0, y0, x1, y1);
  };

  Studio.prototype.markWet = function (x0, y0, x1, y1) {
    x0 = clamp(Math.floor(x0), 0, this.w); y0 = clamp(Math.floor(y0), 0, this.h);
    x1 = clamp(Math.ceil(x1), 0, this.w); y1 = clamp(Math.ceil(y1), 0, this.h);
    if (x1 <= x0 || y1 <= y0) return;
    if (!this.wetBox) {
      // Remember which tool laid the stroke down, so a tool change part-way
      // through cannot flip how it is composited.
      this.wetTool = this.tool;
      this.wetBox = { x0: x0, y0: y0, x1: x1, y1: y1 };
      return;
    }
    var b = this.wetBox;
    if (x0 < b.x0) b.x0 = x0;
    if (y0 < b.y0) b.y0 = y0;
    if (x1 > b.x1) b.x1 = x1;
    if (y1 > b.y1) b.y1 = y1;
  };

  Studio.prototype.strokeTo = function (x, y) {
    var lx = this.lastX, ly = this.lastY;
    var dx = x - lx, dy = y - ly;
    var dist = Math.sqrt(dx * dx + dy * dy);
    var step = Math.max(1, (this.size / 2) * (this.softness > 0.02 ? 0.10 : 0.3));
    if (dist < step) return;
    var n = Math.floor(dist / step);
    for (var i = 1; i <= n; i++) {
      this.stamp(lx + (dx * i * step) / dist, ly + (dy * i * step) / dist);
    }
    this.lastX = lx + (dx * n * step) / dist;
    this.lastY = ly + (dy * n * step) / dist;
    this.render();
  };

  /* Flood fill ---------------------------------------------------------- */
  Studio.prototype.fill = function (sx, sy) {
    if (this.tainted) return;
    this.flushWet();
    var w = this.w, h = this.h;
    sx = clamp(sx | 0, 0, w - 1); sy = clamp(sy | 0, 0, h - 1);

    var image = this.pctx.getImageData(0, 0, w, h);
    var d = image.data;
    var line = this.lineAlpha;

    var tol = this.tolerance;
    var lineMax = 55 + tol * 1.6;       // higher tolerance => leaks through fainter lines
    var colTol = 12 + tol * 2.2;

    var start = (sy * w + sx);
    if (line[start] > lineMax) return;   // clicked on the outline itself

    var sp = start * 4;
    var sr = d[sp], sg = d[sp + 1], sb = d[sp + 2], sa = d[sp + 3];

    function match(i) {
      var p = i * 4;
      var a = d[p + 3];
      if (sa < 10) return a < 10;
      if (a < 10) return false;
      return Math.abs(d[p] - sr) <= colTol &&
             Math.abs(d[p + 1] - sg) <= colTol &&
             Math.abs(d[p + 2] - sb) <= colTol &&
             Math.abs(a - sa) <= colTol;
    }
    function open(i) { return line[i] <= lineMax && match(i); }

    var visited = new Uint8Array(w * h);
    var mask = new Uint8Array(w * h);
    var stack = [start];
    var minX = sx, maxX = sx, minY = sy, maxY = sy;

    while (stack.length) {
      var i = stack.pop();
      if (visited[i]) continue;
      var y = (i / w) | 0;
      var rowStart = y * w;
      var x = i - rowStart;

      while (x > 0 && !visited[i - 1] && open(i - 1)) { i--; x--; }

      var spanUp = false, spanDown = false;
      while (x < w && !visited[i] && open(i)) {
        visited[i] = 1; mask[i] = 1;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;

        if (y > 0) {
          var u = i - w;
          var uo = !visited[u] && open(u);
          if (uo && !spanUp) { stack.push(u); spanUp = true; }
          else if (!uo) { spanUp = false; }
        }
        if (y < h - 1) {
          var dn = i + w;
          var dno = !visited[dn] && open(dn);
          if (dno && !spanDown) { stack.push(dn); spanDown = true; }
          else if (!dno) { spanDown = false; }
        }
        i++; x++;
      }
    }

    // Creep 2px under the anti-aliased outline so no white halo is left behind.
    for (var pass = 0; pass < 2; pass++) {
      var grown = [];
      for (var yy = minY; yy <= maxY; yy++) {
        for (var xx = minX; xx <= maxX; xx++) {
          var k = yy * w + xx;
          if (mask[k] || line[k] <= lineMax || line[k] >= 250) continue;
          if ((xx > 0 && mask[k - 1]) || (xx < w - 1 && mask[k + 1]) ||
              (yy > 0 && mask[k - w]) || (yy < h - 1 && mask[k + w])) grown.push(k);
        }
      }
      for (var gi = 0; gi < grown.length; gi++) mask[grown[gi]] = 1;
      minX = Math.max(0, minX - 1); minY = Math.max(0, minY - 1);
      maxX = Math.min(w - 1, maxX + 1); maxY = Math.min(h - 1, maxY + 1);
    }

    var rgb = hexToRgb(this.color);
    var alpha = Math.round(255 * this.opacity);   // the bucket honours it too
    for (var y2 = minY; y2 <= maxY; y2++) {
      for (var x2 = minX; x2 <= maxX; x2++) {
        var m = y2 * w + x2;
        if (!mask[m]) continue;
        var q = m * 4;
        d[q] = rgb.r; d[q + 1] = rgb.g; d[q + 2] = rgb.b; d[q + 3] = alpha;
      }
    }

    this.pctx.putImageData(image, 0, 0, minX, minY, maxX - minX + 1, maxY - minY + 1);
    this.markDirty(minX, minY, maxX + 1, maxY + 1);
    this.commit();
    this.render();
  };

  /* Zoom ---------------------------------------------------------------- */
  /* Zoom resizes the canvas ELEMENT and lets the stage scroll, rather than
     applying a transform. That matters: every coordinate below is derived from
     getBoundingClientRect(), which already accounts for the new size and for
     scrolling, so the pointer maths needs no zoom term at all and cannot drift
     out of step with the view. */

  Studio.prototype.fitSize = function () {
    // Measure what the canvas would be at fit, without disturbing the zoom.
    var v = this.view;
    var w = v.style.width, h = v.style.height;
    var was = this.stage.classList.contains('is-zoomed');
    v.style.width = ''; v.style.height = '';
    this.stage.classList.remove('is-zoomed');
    var r = v.getBoundingClientRect();
    var out = { w: r.width, h: r.height };
    if (was) {
      v.style.width = w; v.style.height = h;
      this.stage.classList.add('is-zoomed');
    }
    return out;
  };

  Studio.prototype.applyZoom = function () {
    var v = this.view;
    if (this.zoom <= 1.001) {
      this.zoom = 1;
      v.style.width = ''; v.style.height = '';
      this.stage.classList.remove('is-zoomed');
      this.stage.scrollLeft = 0;
      this.stage.scrollTop = 0;
      return;
    }
    var fit = this.fitSize();
    this.stage.classList.add('is-zoomed');
    v.style.width = (fit.w * this.zoom) + 'px';
    v.style.height = (fit.h * this.zoom) + 'px';
  };

  /* Zoom about a fixed point on the artwork, so whatever is under the pointer
     (or under the middle of the screen) stays put. */
  Studio.prototype.setZoom = function (z, anchorX, anchorY) {
    z = clamp(z, 1, this.MAX_ZOOM);
    if (Math.abs(z - this.zoom) < 0.001) return;

    var r = this.view.getBoundingClientRect();
    if (anchorX == null) { anchorX = r.left + r.width / 2; anchorY = r.top + r.height / 2; }

    var before = this.displayRect();
    var ax = (anchorX - before.left) / before.scale;
    var ay = (anchorY - before.top) / before.scale;

    this.zoom = z;
    this.applyZoom();

    var after = this.displayRect();
    this.stage.scrollLeft += (after.left + ax * after.scale) - anchorX;
    this.stage.scrollTop += (after.top + ay * after.scale) - anchorY;

    this.updateUi();
  };

  Studio.prototype.zoomBy = function (factor, anchorX, anchorY) {
    this.setZoom(this.zoom * factor, anchorX, anchorY);
  };

  /* Pointer input ------------------------------------------------------- */
  /* Where the drawing actually sits on screen.
     The element box is not always the drawing: any CSS that clamps one axis
     (a max-height, say) leaves the artwork letterboxed inside a larger box.
     Measuring the box instead of the artwork is what makes a brush land away
     from the cursor, so derive the offset and scale properly every time. */
  Studio.prototype.displayRect = function () {
    var r = this.view.getBoundingClientRect();
    var scale = Math.min(r.width / this.w, r.height / this.h);
    if (!isFinite(scale) || scale <= 0) scale = 1;
    return {
      scale: scale,
      left: r.left + (r.width - this.w * scale) / 2,
      top: r.top + (r.height - this.h * scale) / 2
    };
  };

  Studio.prototype.toCanvas = function (ev) {
    var d = this.displayRect();
    return { x: (ev.clientX - d.left) / d.scale, y: (ev.clientY - d.top) / d.scale };
  };

  /* A ring showing the brush footprint. It sits on top of the ordinary
     crosshair rather than replacing it: hiding the system cursor would mean any
     failure to draw the ring leaves the shopper with no cursor at all. */
  Studio.prototype.moveRing = function (ev) {
    var ring = this.ring;
    if (!ring) return;
    if (ev) {
      this._flashing = false;
      this._lastPointer = { clientX: ev.clientX, clientY: ev.clientY, pointerType: ev.pointerType };
    }
    var e = ev || this._lastPointer;
    if (!e || !this.w || this.tool === 'fill' || e.pointerType === 'touch') {
      this.hideRing();
      return;
    }
    var d = this.displayRect();
    var px = Math.max(10, this.size * d.scale);
    ring.hidden = false;
    ring.style.width = px + 'px';
    ring.style.height = px + 'px';
    /* Placed inside the stage rather than against the viewport. A fixed ring
       looks right in isolation, but any ancestor carrying a transform (Dawn
       animates whole sections) makes `fixed` resolve against that ancestor
       instead of the window, and the ring lands somewhere else entirely — off
       screen, as often as not. Measuring the stage and adding its scroll makes
       the maths immune to that, zoomed or not. */
    var s = this.stage.getBoundingClientRect();
    ring.style.left = (e.clientX - s.left + this.stage.scrollLeft) + 'px';
    ring.style.top = (e.clientY - s.top + this.stage.scrollTop) + 'px';
  };

  /* Show the footprint briefly when the sliders move and the pointer is not
     over the picture — otherwise the size change has no visible effect. */
  Studio.prototype.flashRing = function () {
    if (this._lastPointer && !this._flashing) return;
    var r = this.view.getBoundingClientRect();
    var self = this;
    this._flashing = true;
    this._lastPointer = {
      clientX: r.left + r.width / 2,
      clientY: r.top + r.height / 2,
      pointerType: 'mouse'
    };
    this.moveRing(null);
    clearTimeout(this._flashT);
    this._flashT = setTimeout(function () {
      self._flashing = false;
      self._lastPointer = null;
      self.hideRing();
    }, 1400);
  };

  Studio.prototype.hideRing = function () {
    if (this.ring) this.ring.hidden = true;
  };

  /* Is the pointer over the artwork, whatever it technically landed on? */
  Studio.prototype.overPicture = function (ev) {
    if (!this.w) return false;
    var p = this.toCanvas(ev);
    return p.x >= 0 && p.y >= 0 && p.x <= this.w && p.y <= this.h;
  };

  Studio.prototype.endStroke = function () {
    if (!this.drawing) return;
    this.drawing = false;
    try { this.view.releasePointerCapture(this.pointerId); } catch (e) {}
    this.pointerId = null;
    this.commit();
    this.render();
  };

  Studio.prototype.bindCanvas = function () {
    var self = this;
    var v = this.view;
    // Pointers are tracked on the stage, not the canvas, so a finger that lands
    // just off the picture still counts towards a pinch. Painting still only
    // starts on the canvas itself.
    var host = this.stage;

    function pinch() {
      var pts = [];
      self.pointers.forEach(function (pt) { pts.push(pt); });
      var a = pts[0], b = pts[1];
      var dx = b.x - a.x, dy = b.y - a.y;
      return {
        dist: Math.max(1, Math.sqrt(dx * dx + dy * dy)),
        midX: (a.x + b.x) / 2,
        midY: (a.y + b.y) / 2
      };
    }

    host.addEventListener('pointerdown', function (ev) {
      if (!self.w) return;
      if (ev.pointerType === 'mouse' && ev.button !== 0) return;
      self.pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

      if (self.pointers.size === 2) {
        // A second finger means pinch, not paint. Undo the dab the first finger
        // just made, so starting a zoom never leaves a mark behind.
        var hadWork = self.history.length;
        self.endStroke();
        if (self.history.length > self._histAtStroke) self.undo();
        void hadWork;
        var g = pinch();
        self.gesture = { dist: g.dist, midX: g.midX, midY: g.midY, zoom: self.zoom };
        self.hideRing();
        return;
      }
      if (self.pointers.size > 2) return;
      if (ev.target !== v) return;          // margin taps do not paint

      ev.preventDefault();
      var p = self.toCanvas(ev);
      if (self.tool === 'fill') { self.fill(Math.round(p.x), Math.round(p.y)); return; }

      self._histAtStroke = self.history.length;
      self.drawing = true;
      self.pointerId = ev.pointerId;
      try { v.setPointerCapture(ev.pointerId); } catch (e) {}
      self.lastX = p.x; self.lastY = p.y;
      self.stamp(p.x, p.y);
      self.render();
    });

    host.addEventListener('pointermove', function (ev) {
      if (self.pointers.has(ev.pointerId)) {
        self.pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      }

      // Two fingers: pinch to zoom, drag to pan.
      if (self.gesture && self.pointers.size >= 2) {
        ev.preventDefault();
        var g = pinch();
        self.setZoom(self.gesture.zoom * (g.dist / self.gesture.dist), g.midX, g.midY);
        self.stage.scrollLeft -= (g.midX - self.gesture.midX);
        self.stage.scrollTop -= (g.midY - self.gesture.midY);
        self.gesture.midX = g.midX;
        self.gesture.midY = g.midY;
        return;
      }

      /* Show the ring by where the pointer actually is, not by what it hit.
         Testing ev.target means anything the theme happens to lay over the
         picture — an overlay, a badge, a stray wrapper — silently kills the
         ring, and the shopper is left with no sense of the brush size. */
      if (self.overPicture(ev)) self.moveRing(ev); else self.hideRing();
      if (!self.drawing || ev.pointerId !== self.pointerId) return;
      ev.preventDefault();
      var evs = (ev.getCoalescedEvents ? ev.getCoalescedEvents() : null) || [ev];
      for (var i = 0; i < evs.length; i++) {
        var p = self.toCanvas(evs[i]);
        self.strokeTo(p.x, p.y);
      }
    });

    v.addEventListener('pointerenter', function (ev) { self.moveRing(ev); });

    function release(ev) {
      if (ev) self.pointers.delete(ev.pointerId);
      if (self.pointers.size < 2) self.gesture = null;
      if (!ev || ev.pointerId === self.pointerId) self.endStroke();
    }
    host.addEventListener('pointerup', release);
    host.addEventListener('pointercancel', release);
    host.addEventListener('pointerleave', function (ev) {
      self._lastPointer = null;
      self.hideRing();
      release(ev);
    });

    // Ctrl/Cmd + wheel, and trackpad pinch, zoom about the pointer. A plain
    // wheel is left alone so the page still scrolls normally.
    host.addEventListener('wheel', function (ev) {
      if (!ev.ctrlKey && !ev.metaKey) return;
      ev.preventDefault();
      self.zoomBy(Math.exp(-ev.deltaY * 0.0025), ev.clientX, ev.clientY);
    }, { passive: false });
  };

  /* The tool panel is a drawer on small screens, so it never sits on top of
     the picture while you are colouring. */
  Studio.prototype.setDrawer = function (open) {
    var panel = this.el.querySelector('[data-cp-panel]');
    var back = this.el.querySelector('[data-cp-drawer-backdrop]');
    if (!panel) return;
    panel.classList.toggle('is-open', !!open);
    if (back) back.hidden = !open;
    var toggle = this.el.querySelector('[data-cp-drawer-open]');
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) this.revealPicture(panel);
  };

  /* The drawer takes the lower part of the screen, and the picture normally
     sits right above the bar it was opened from — which would leave it hidden
     behind the drawer. Scroll it up so a good part of the artwork stays in
     view: a colour is worth choosing against the work, not blind. */
  Studio.prototype.revealPicture = function (panel) {
    if (!this.view) return;
    var vp = window.innerHeight || 0;
    if (!vp) return;
    // The panel is mid-transition here, so measure its height, not its top.
    var css = window.getComputedStyle(panel);
    if (css.position !== 'fixed') return;   // desktop: the panel is in the flow
    var drawerTop = vp - panel.offsetHeight;
    var r = this.view.getBoundingClientRect();
    var visible = Math.min(r.bottom, drawerTop) - Math.max(r.top, 0);
    if (visible >= Math.min(r.height, drawerTop * 0.6)) return;
    var delta = Math.round(r.top - 8);
    if (!delta) return;
    if (window.scrollBy) {
      try { window.scrollBy({ top: delta, left: 0, behavior: 'smooth' }); }
      catch (e) { window.scrollBy(0, delta); }
    }
  };

  /* UI ------------------------------------------------------------------ */
  Studio.prototype.bindUi = function () {
    if (this._uiBound) { this.updateUi(); return; }
    this._uiBound = true;
    var self = this;
    var el = this.el;

    this.bindCanvas();

    el.querySelectorAll('[data-cp-tool]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.tool = btn.getAttribute('data-cp-tool');
        self.updateUi();
        self.setDrawer(false);
      });
    });

    var size = el.querySelector('[data-cp-size]');
    size.addEventListener('input', function () {
      self.size = parseInt(size.value, 10);
      self.updateUi();
      self.flashRing();
    });

    var soft = el.querySelector('[data-cp-softness]');
    soft.addEventListener('input', function () {
      self.softness = parseInt(soft.value, 10) / 100;
      self.updateUi();
      self.flashRing();
    });

    var op = el.querySelector('[data-cp-opacity]');
    if (op) {
      op.addEventListener('input', function () {
        // Changing it mid-stroke would re-composite work already laid down, so
        // settle the stroke in progress first.
        self.commit();
        self.opacity = clamp(parseInt(op.value, 10) / 100, 0.02, 1);
        self.updateUi();
      });
    }

    el.querySelectorAll('[data-cp-zoom-in]').forEach(function (b) {
      b.addEventListener('click', function () { self.zoomBy(1.4); });
    });
    el.querySelectorAll('[data-cp-zoom-out]').forEach(function (b) {
      b.addEventListener('click', function () { self.zoomBy(1 / 1.4); });
    });
    el.querySelectorAll('[data-cp-zoom-fit]').forEach(function (b) {
      b.addEventListener('click', function () { self.setZoom(1); });
    });

    var drawerOpen = el.querySelector('[data-cp-drawer-open]');
    if (drawerOpen) drawerOpen.addEventListener('click', function () { self.setDrawer(true); });
    el.querySelectorAll('[data-cp-drawer-close]').forEach(function (b) {
      b.addEventListener('click', function () { self.setDrawer(false); });
    });
    var backdrop = el.querySelector('[data-cp-drawer-backdrop]');
    if (backdrop) backdrop.addEventListener('click', function () { self.setDrawer(false); });

    var tol = el.querySelector('[data-cp-tolerance]');
    tol.addEventListener('input', function () { self.tolerance = parseInt(tol.value, 10); });

    // Palette
    var wrap = el.querySelector('[data-cp-palette]');
    var palette = (this.app.data.settings.palette && this.app.data.settings.palette.length)
      ? this.app.data.settings.palette : DEFAULT_PALETTE;
    wrap.innerHTML = '';
    palette.forEach(function (hex) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cp-swatch';
      b.style.setProperty('--cp-swatch', hex);
      b.setAttribute('data-cp-swatch', hex);
      b.setAttribute('aria-label', hex);
      b.addEventListener('click', function () {
        self.color = hex;
        if (self.tool === 'eraser') self.tool = 'brush';
        self.updateUi();
        self.setDrawer(false);
      });
      wrap.appendChild(b);
    });

    var custom = el.querySelector('[data-cp-custom]');
    custom.addEventListener('input', function () {
      self.color = custom.value;
      if (self.tool === 'eraser') self.tool = 'brush';
      self.updateUi();
    });

    // Undo appears in both the panel and the phone bar, so wire every copy.
    el.querySelectorAll('[data-cp-undo]').forEach(function (b) {
      b.addEventListener('click', function () { self.undo(); });
    });
    el.querySelectorAll('[data-cp-redo]').forEach(function (b) {
      b.addEventListener('click', function () { self.redo(); });
    });
    el.querySelectorAll('[data-cp-clear]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (window.confirm(self.t.clear_confirm)) self.clear();
      });
    });

    var dl = el.querySelector('[data-cp-download]');
    if (dl) dl.addEventListener('click', function () { self.download(); });
    var pr = el.querySelector('[data-cp-print]');
    if (pr) pr.addEventListener('click', function () { self.print(); });
    var sh = el.querySelector('[data-cp-share]');
    if (sh) sh.addEventListener('click', function () { self.share(); });
    var en = el.querySelector('[data-cp-enter]');
    if (en) en.addEventListener('click', function () { self.openEntry(); });

    this.bindEntry();

    document.addEventListener('keydown', function (ev) {
      if (!self.w) return;
      var tag = (ev.target && ev.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      var meta = ev.ctrlKey || ev.metaKey;
      if (meta && ev.key.toLowerCase() === 'z') {
        ev.preventDefault();
        if (ev.shiftKey) self.redo(); else self.undo();
      } else if (meta && ev.key.toLowerCase() === 'y') {
        ev.preventDefault(); self.redo();
      } else if (ev.key === '[') {
        size.value = Math.max(parseInt(size.min, 10), self.size - 4);
        size.dispatchEvent(new Event('input'));
      } else if (ev.key === ']') {
        size.value = Math.min(parseInt(size.max, 10), self.size + 4);
        size.dispatchEvent(new Event('input'));
      } else if (ev.key === 'b' || ev.key === 'e' || ev.key === 'f') {
        self.tool = { b: 'brush', e: 'eraser', f: 'fill' }[ev.key];
        self.updateUi();
      } else if (ev.key === '+' || ev.key === '=') {
        ev.preventDefault(); self.zoomBy(1.4);
      } else if (ev.key === '-' || ev.key === '_') {
        ev.preventDefault(); self.zoomBy(1 / 1.4);
      } else if (ev.key === '0') {
        ev.preventDefault(); self.setZoom(1);
      } else if (ev.key === 'Escape') {
        self.setDrawer(false);
      }
    });
  };

  Studio.prototype.updateUi = function () {
    var el = this.el;
    var self = this;

    el.querySelectorAll('[data-cp-tool]').forEach(function (b) {
      var on = b.getAttribute('data-cp-tool') === self.tool;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    el.setAttribute('data-cp-active-tool', this.tool);

    var size = el.querySelector('[data-cp-size]');
    var soft = el.querySelector('[data-cp-softness]');
    if (parseInt(size.value, 10) !== this.size) size.value = this.size;
    if (parseInt(soft.value, 10) !== Math.round(this.softness * 100)) soft.value = Math.round(this.softness * 100);

    var op = el.querySelector('[data-cp-opacity]');
    var opPct = Math.round(this.opacity * 100);
    if (op && parseInt(op.value, 10) !== opPct) op.value = opPct;
    var opOut = el.querySelector('[data-cp-opacity-out]');
    if (opOut) opOut.textContent = opPct + '%';

    el.querySelector('[data-cp-size-out]').textContent = this.size;
    var preview = el.querySelector('[data-cp-preview]');
    // Show the brush at the size it will actually appear on screen, so the
    // swatch and the ring on the picture agree.
    var onScreen = Math.max(3, this.w ? this.size * this.displayRect().scale : this.size);
    // Blob core plus blur is sized to land on the nominal diameter, so the
    // ring around it really does mark where the paint stops.
    preview.style.setProperty('--cp-preview-size', onScreen + 'px');
    preview.style.setProperty('--cp-preview-core', (onScreen * (1 - this.softness * 0.5)) + 'px');
    preview.style.setProperty('--cp-preview-blur', (onScreen * this.softness * 0.25) + 'px');
    preview.style.setProperty('--cp-preview-color', this.tool === 'eraser' ? '#d9d9d9' : this.color);
    preview.style.setProperty('--cp-preview-alpha', this.opacity);

    el.querySelectorAll('[data-cp-swatch]').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-cp-swatch').toLowerCase() === self.color.toLowerCase() && self.tool !== 'eraser');
    });
    var custom = el.querySelector('[data-cp-custom]');
    if (custom.value.toLowerCase() !== this.color.toLowerCase()) custom.value = this.color;

    el.querySelectorAll('[data-cp-undo]').forEach(function (b) { b.disabled = self.history.length === 0; });
    el.querySelectorAll('[data-cp-redo]').forEach(function (b) { b.disabled = self.future.length === 0; });

    var zoomText = Math.round(this.zoom * 100) + '%';
    el.querySelectorAll('[data-cp-zoom-level]').forEach(function (n) { n.textContent = zoomText; });
    el.querySelectorAll('[data-cp-zoom-out]').forEach(function (b) { b.disabled = self.zoom <= 1.001; });
    el.querySelectorAll('[data-cp-zoom-in]').forEach(function (b) {
      b.disabled = self.zoom >= self.MAX_ZOOM - 0.001;
    });

    var bar = el.querySelector('[data-cp-bar-swatch]');
    if (bar) {
      bar.style.background = this.tool === 'eraser' ? '#d9d9d9' : this.color;
      bar.style.opacity = Math.max(0.25, this.opacity);
    }

    el.querySelectorAll('[data-cp-pick]').forEach(function (a) {
      var on = a.getAttribute('data-cp-pick') === (self.page && self.page.id);
      a.classList.toggle('is-active', on);
      a.setAttribute('aria-current', on ? 'true' : 'false');
    });

    this.moveRing(null);
  };

  /* True once anything has been painted — used to warn before switching away. */
  Studio.prototype.hasWork = function () {
    return this.history.length > 0 || this.future.length > 0;
  };

  /* Output -------------------------------------------------------------- */
  Studio.prototype.filename = function () {
    var base = (this.page.title || 'colouring-page')
      .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (base || 'colouring-page') + '.png';
  };

  Studio.prototype.toBlob = function (cb) {
    var flat = this.flatten();
    if (flat.toBlob) flat.toBlob(cb, 'image/png');
    else cb(null);
  };

  Studio.prototype.download = function () {
    var self = this;
    this.toBlob(function (blob) {
      if (!blob) return;
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = self.filename();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
    });
  };

  Studio.prototype.print = function () {
    var url = this.flatten().toDataURL('image/png');
    var frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;opacity:0;border:0;';
    document.body.appendChild(frame);
    var doc = frame.contentWindow.document;
    doc.open();
    doc.write('<!doctype html><html><head><meta charset="utf-8"><title>' +
      (this.page.title || '') + '</title><style>@page{margin:12mm}html,body{margin:0;padding:0}' +
      'img{display:block;width:100%;height:auto}</style></head><body><img src="' + url + '"></body></html>');
    doc.close();
    var img = doc.images[0];
    var go = function () {
      try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch (e) {}
      setTimeout(function () { frame.remove(); }, 1000);
    };
    if (img.complete) setTimeout(go, 120); else img.onload = function () { setTimeout(go, 120); };
  };

  Studio.prototype.share = function () {
    var self = this;
    var pageUrl = window.location.href.split('?')[0];
    this.toBlob(function (blob) {
      var file = blob ? new File([blob], self.filename(), { type: 'image/png' }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        navigator.share({
          files: [file],
          title: self.page.title || self.t.share,
          text: self.t.share_text
        }).catch(function () {});
        return;
      }
      if (navigator.share) {
        navigator.share({ title: self.page.title || '', text: self.t.share_text, url: pageUrl }).catch(function () {});
        return;
      }
      self.download();
    });
  };

  /* Competition entry ---------------------------------------------------- */
  Studio.prototype.bindEntry = function () {
    var self = this;
    this.modal = this.el.querySelector('[data-cp-modal]');
    if (!this.modal) return;
    this.form = this.modal.querySelector('form');

    this.modal.querySelectorAll('[data-cp-modal-close]').forEach(function (b) {
      b.addEventListener('click', function () { self.closeEntry(); });
    });
    this.modal.addEventListener('click', function (ev) {
      if (ev.target === self.modal) self.closeEntry();
    });
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && !self.modal.hidden) self.closeEntry();
    });
    this.form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      self.submitEntry();
    });
  };

  Studio.prototype.openEntry = function () {
    if (!this.modal) return;
    var prev = this.modal.querySelector('[data-cp-entry-preview]');
    try { prev.src = this.flatten().toDataURL('image/png'); } catch (e) {}
    this.modal.hidden = false;
    document.body.classList.add('cp-modal-open');
    var first = this.form.querySelector('input:not([type=hidden])');
    if (first) first.focus();
  };

  Studio.prototype.closeEntry = function () {
    if (!this.modal) return;
    this.modal.hidden = true;
    document.body.classList.remove('cp-modal-open');
  };

  Studio.prototype.submitEntry = function () {
    var self = this;
    var s = this.app.data.settings;
    var form = this.form;
    var msg = this.modal.querySelector('[data-cp-entry-message]');
    var btn = form.querySelector('[type=submit]');

    if (form.querySelector('[name=_gotcha]').value) { this.closeEntry(); return; }
    if (!s.entry_endpoint) {
      msg.hidden = false;
      msg.className = 'cp-msg cp-msg--error';
      msg.textContent = this.t.submit_error;
      return;
    }

    btn.disabled = true;
    msg.hidden = false;
    msg.className = 'cp-msg';
    msg.textContent = this.t.sending;

    var fields = {
      name: form.elements.name.value.trim(),
      email: form.elements.email.value.trim(),
      age: form.elements.age ? form.elements.age.value : '',
      consent: form.elements.consent && form.elements.consent.checked ? 'yes' : 'no',
      page_title: this.page.title || '',
      page_url: window.location.href,
      shop: s.shop || '',
      submitted_at: new Date().toISOString()
    };

    this.toBlob(function (blob) {
      var done = function (ok) {
        btn.disabled = false;
        msg.className = ok ? 'cp-msg cp-msg--ok' : 'cp-msg cp-msg--error';
        msg.textContent = ok ? (s.entry_success || self.t.sent) : self.t.submit_error;
        if (ok) {
          form.reset();
          setTimeout(function () { self.closeEntry(); }, 2600);
        }
      };

      var request;
      if (s.entry_format === 'json') {
        var reader = new FileReader();
        reader.onload = function () {
          fields.filename = self.filename();
          fields.image_base64 = String(reader.result).split(',')[1] || '';
          fetch(s.entry_endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(fields)
          }).then(function (r) { done(r.ok); }).catch(function () { done(false); });
        };
        reader.onerror = function () { done(false); };
        reader.readAsDataURL(blob);
        return;
      }

      var fd = new FormData();
      Object.keys(fields).forEach(function (k) { fd.append(k, fields[k]); });
      if (blob) fd.append('file', blob, self.filename());
      request = fetch(s.entry_endpoint, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
      request.then(function (r) { done(r.ok); }).catch(function () { done(false); });
    });
  };

  /* ------------------------------------------------------------------ */
  /* App: picks the picture and keeps the studio pointed at it            */
  /* ------------------------------------------------------------------ */

  function App(root) {
    this.root = root;
    var node = root.querySelector('[data-colouring-data]');
    this.data = JSON.parse(node.textContent);
    this.t = this.data.i18n || {};
    this.studio = root.querySelector('[data-colouring-studio]');
    this.sectionId = root.getAttribute('data-section-id');
    if (!this.studio || !this.data.pages.length) return;

    var self = this;

    // Each thumbnail is a real link, so it still works without JavaScript;
    // with JavaScript we swap the picture in place instead of reloading.
    root.querySelectorAll('[data-cp-pick]').forEach(function (a) {
      a.addEventListener('click', function (ev) {
        ev.preventDefault();
        self.open(a.getAttribute('data-cp-pick'), true);
      });
    });

    if (this.data.settings.hide_chrome) document.body.classList.add('cp-studio-active');

    this.open(this.startId(), false);
    window.addEventListener('popstate', function () { self.open(self.startId(), false); });
  }

  App.prototype.startId = function () {
    var params = new URLSearchParams(window.location.search);
    var wanted = params.get('colour');
    var section = params.get('cp_section');
    if (wanted && (!section || section === this.sectionId) && this.pageById(wanted)) return wanted;
    return this.data.pages[0].id;      // first picture is pre-selected on load
  };

  App.prototype.pageById = function (id) {
    for (var i = 0; i < this.data.pages.length; i++) {
      if (this.data.pages[i].id === id) return this.data.pages[i];
    }
    return null;
  };

  App.prototype.open = function (id, fromClick) {
    var page = this.pageById(id);
    if (!page) return;
    var s = this.studioInstance;
    if (s && s.page && s.page.id === id) return;
    if (fromClick && s && s.hasWork() && !window.confirm(this.t.switch_confirm)) return;

    var cta = this.studio.querySelector('[data-cp-product]');
    if (cta) {
      if (page.product_url) {
        cta.hidden = false;
        cta.href = page.product_url;
        var label = cta.querySelector('[data-cp-product-label]');
        if (label && page.product_title) label.textContent = page.product_title;
      } else {
        cta.hidden = !this.data.settings.cta_url;
      }
    }

    if (s) {
      s.page = page;
      s.history.length = 0;
      s.future.length = 0;
      s.historyBytes = 0;
      s.dirty = null;
      s.tainted = false;
      s.load();
    } else {
      this.studioInstance = new Studio(this, page);
    }

    // Keep the address bar on the picture being coloured, without stacking
    // history entries every time someone browses the thumbnails.
    if (fromClick && window.history && window.history.replaceState) {
      var url = window.location.pathname + '?colour=' + encodeURIComponent(id);
      window.history.replaceState({}, '', url);
    }
  };

  function boot() {
    document.querySelectorAll('[data-colouring-root]').forEach(function (root) {
      if (root.__cpApp) return;
      root.__cpApp = new App(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  window.ColouringPagesBoot = boot;
})();
